import React, { useEffect, useRef, useState } from "react";
import { RetellWebClient } from "retell-client-js-sdk";
import { AwazWebClient } from "./webrtc/awaz/web_client";
import { START_CONVERSATION } from '../services/agents';
import { notifications, showNotification } from "@mantine/notifications";

interface RegisterCallResponse {
  accessToken?: string;
  callId?: string;
  transport?: "livekit" | "gateway";
  iceServers?: RTCIceServer[];
  url?: string;
  access_token?: string;
  call_id?: string;
  ice_servers?: RTCIceServer[];
}

const useCallAgent = (
  { own = false, onFinish = null }
) : [
  boolean,
  ({ agentId, own, onFinish }: { agentId: string; own?: boolean; onFinish?: ({callId}) => void }) => void,
  boolean,
] => {
  const webClient = useRef<RetellWebClient>();
  const [isCalling, setIsCalling] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const callIdRef = useRef<string>();

  // Initialize the SDK
  useEffect(() => {
    webClient.current = new RetellWebClient();
    // Setup event listeners
    webClient.current.on("call_started", () => {
      console.log("conversationStarted");
      setIsCalling(true)
    });

    webClient.current.on("audio", (audio: Uint8Array) => {
      console.log("There is audio");
    });

    webClient.current.on("call_ended", () => {
      // console.log("Closed with code:", code, ", reason:", reason);
      onFinish && onFinish({ callId: callIdRef.current });
      setIsCalling(false); // Update button to "Start" when conversation ends
    });

    webClient.current.on("error", (error) => {
      console.error("An error occurred:", error);
      setIsCalling(false); // Update button to "Start" in case of error
    });

    webClient.current.on("update", (update) => {
      // Print live transcript as needed
      // console.log("update", update);
    });
  }, [own]);

  const toggleConversation = async ({ agentId }) => {
    if (isCalling) {
      webClient.current.stopCall();
    } else {
      setLoading(true);
      const registerCallResponse = await registerCall(agentId);
      if (!registerCallResponse) {
        setLoading(false);
        return;
      }

      const accessToken = registerCallResponse.accessToken ?? registerCallResponse.access_token;
      const callId = registerCallResponse.callId ?? registerCallResponse.call_id;
      const iceServers = registerCallResponse.iceServers ?? registerCallResponse.ice_servers;
      if (!accessToken || !callId) {
        notifications.show({
          message: "The call service did not return valid connection details.",
          color: "red",
        });
        setLoading(false);
        return;
      }

      callIdRef.current = callId;
      webClient.current
        .startCall({
          accessToken,
          callId,
          transport: registerCallResponse.transport,
          iceServers,
          url: registerCallResponse.url,
          emitRawAudioSamples: false,
          sampleRate: 24000,
          captureDeviceId: "default",
        })
        .then(() => {
          setIsCalling(true);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  };

  async function registerCall(
    agentId: string,
  ): Promise<RegisterCallResponse | undefined> {
    try {
      const response = await START_CONVERSATION(agentId);
      return response;
    } catch (err) {
      console.log(err);
      notifications.show({ message: err.message, color: "red" })
    }
  }

  return [
    isCalling,
    toggleConversation,
    isLoading,
  ];
};

export default useCallAgent;